import { IPoint } from "./IPoint.ts";
import { IResources } from "./IResources.ts";

/**
 * Game configuration DTO read from the building-definitions.json
 */
export interface IBuildingsDefinition {
    buildings: BuildingConfig[];
    baseCamp: BaseCampConfig;
    
    playerConst: {
      baseMaxCapacity: number;
    }
  }  

  export type BuildersConfig = Omit<SlotsConfig, 'spacing'>;

  export type BaseCampConfig = {
    slots: SlotsConfig;
    foodConsumptionLabel: { x: number, y: number };
    builders: BuildersConfig;
  }
  
  export type SlotsConfig ={
    size: number;
    spacing: number;
    slotPositions: SlotPosition[];  
  }

  export type SlotPosition = {
    id: number;
    x: number;
    y: number;
  }
  
  export interface ResourceCost {
    wood: number;
    stone: number;
    copper: number;
  }
  
  export interface BuildingAction {
    name: string;
    cost: ResourceCost;
  }
  
  export interface BuildingConfig {
    id: number;
    resource: ResourceString;
    name: string;
    description: string;
    production: number;
    workers: number;
    /**
     * Upgrade duration seconds for level 6, lower levels are built immediately. 
     * For levels > 6 the duration increases: (building level - 5) * 1.5 * duration .
     */
    duration: number,
    cost: ResourceCost;
    slots: number[];
    action: BuildingAction;
    manaStartAfter?: number;
  }
  
  type ResourceString = "wood" | "stone" | "copper" | "food" | "housing" | "population" | "storage" | "";


  export interface IMapDefinition {
    eventLocations: IEventLocation[]; 
}


/**
 * Places on map players can attack.
 */
export interface IEventLocation {
  id: number;
  difficulty: number;
  name: string;
  description?: string;
  position: IPoint;
  npcGroups: INpcGroup[];
}

export interface INpcGroup{
  id: number;
  count: number;  
}

/**
 * Supported unit traits.
 */
export enum TroopTrait {
  Footman,
  Mounted,
  Ranged,
}

/***
 * Represents the base for all combat units (NPC and player).
 */
export interface ICombatUnit {
  id: number;
  name: string;
  stats: ICombatStats;
  trait: TroopTrait;
}

/**
 * Troops DTO read from troop-definitions.json
 */
export interface ITroopDefinition extends ICombatUnit { 
  description: string; 
  trainingCost: IResources;
  /**
   * Training duration in seconds.
   */
  trainingDuration: number;
}

export interface ICombatStats {
  hp: number;
  attack: number;
  defense: number;
  armor: number;
  speed: number;
}

export type DropItemResource = "wood" | "stone" | "copper" | "food" | "mana";
export enum DropItemCategory {
  Resource = "resource",
  Buff = "buff",
  Material = "material",
  Equipment = "equipment"
};
export enum EquipmentSocket {
  Head,
  Neckless,
  Body,
  Cape,
  Ring,
  Leggings,
  Boots,
}
export interface IDropItemDefinition {
  id: number;
  name: string;
  description?: string;
  amount: number;
  category: DropItemCategory;
  /**
   * The type of the drop item depending on category:
   * - Resource: type DropItemResource
   * - Buff: na
   * - Material: na
   * - Equipment: enum EquipmentSlot
   */  
  type: EquipmentSocket | DropItemResource;

  effects?: IDropItemEffect[];
}

export interface IDropItemEffect extends Partial<ICombatStats>{
  /**
   * Duration in hours.
   */
  duration: number;  
}

export interface IOnlineRewardDefinition extends IResources{
  minutes: number;
}